import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from '@mantine/hooks';
import { MORPH_EASE, useMorphDialog } from '@srm/ui/hooks/useMorphDialog';
import TourTooltip from './TourTooltip';
import { TourControlsContext, useTourControls } from './TourControlsContext';
import type { ITourPresentation, TourPresentationStore } from './TourPresentationContext';
import { getTourOrigin } from './tourOrigin';
import { resolveTourTarget, type ITourRect } from './tourGeometry';
import { useTourGeometry } from './useTourGeometry';
import './TourMotionLayer.scss';

interface ITourMotionLayerProps {
    store: TourPresentationStore;
    tourId: string | null;
    running: boolean;
    stepIndex: number;
}

export default function TourMotionLayer({ store, tourId, running, stepIndex }: ITourMotionLayerProps) {
    const incoming = useSyncExternalStore(store.subscribe, store.getSnapshot);
    const [displayed, setDisplayed] = useState<ITourPresentation | null>(null);
    const anchorRef = useRef<HTMLElement | null>(null);
    const coverRef = useRef<SVGPathElement | null>(null);
    const outlineRef = useRef<SVGPathElement | null>(null);
    const geometryRef = useRef<ITourRect | null>(null);
    const openedRef = useRef(false);
    const wasReadyRef = useRef(false);
    const openingAnimationRef = useRef<Animation | null>(null);
    const reducedMotion = useReducedMotion();
    const controls = useTourControls();
    const retainedControls = useRef(controls);
    if (running) retainedControls.current = controls;
    const ready = !!incoming && incoming.tourId === tourId && running;
    const displayedStep = displayed?.props.step;

    useLayoutEffect(() => {
        const starting = ready && !wasReadyRef.current;
        wasReadyRef.current = ready;
        if (!ready || !incoming) {
            openingAnimationRef.current?.cancel();
            return;
        }
        if (starting || displayed?.tourId !== incoming.tourId) {
            openedRef.current = false;
            geometryRef.current = null;
            anchorRef.current = getTourOrigin(incoming.tourId) ?? resolveTourTarget(incoming.props.step.target);
        }
    }, [ready, incoming, displayed?.tourId]);

    const { mounted, closing, dialogRef } = useMorphDialog({
        opened: ready,
        anchorRef,
        openDuration: 0,
        onClosed: () => {
            if (anchorRef.current?.isConnected) anchorRef.current.focus({ preventScroll: true });
        },
    });

    useLayoutEffect(() => {
        if (!mounted) {
            openedRef.current = false;
            openingAnimationRef.current?.cancel();
            openingAnimationRef.current = null;
        }
    }, [mounted]);

    const onPositioned = useCallback(() => {
        const card = dialogRef.current;
        if (openedRef.current || !card) return;
        openedRef.current = true;
        const anchor = anchorRef.current;
        if (reducedMotion || !anchor || !card.animate) return;
        const from = anchor.getBoundingClientRect();
        const to = card.getBoundingClientRect();
        openingAnimationRef.current = card.animate(
            [
                {
                    clipPath: `inset(0 ${Math.max(0, to.width - from.width)}px ${Math.max(0, to.height - from.height)}px 0 round 10px)`,
                    transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)`,
                    opacity: 0.4,
                },
                { clipPath: 'inset(0 0 0 0 round 16px)', transform: 'none', opacity: 1 },
            ],
            { duration: 380, easing: MORPH_EASE },
        );
    }, [dialogRef, reducedMotion]);

    useLayoutEffect(() => {
        if (!ready || !incoming) return;
        const element = dialogRef.current?.firstElementChild as HTMLElement | null;
        if (
            incoming.tourId === displayed?.tourId &&
            incoming.props.index !== displayed.props.index &&
            !reducedMotion &&
            element?.animate
        ) {
            const exit = element.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 90, fill: 'forwards' });
            const timer = window.setTimeout(() => {
                setDisplayed(incoming);
                exit.cancel();
            }, 90);
            return () => {
                clearTimeout(timer);
                exit.cancel();
            };
        }
        setDisplayed(incoming);
    }, [ready, incoming, reducedMotion, dialogRef, displayed]);

    useTourGeometry({
        step: displayed?.props.step,
        mounted,
        closing,
        reducedMotion,
        cardRef: dialogRef,
        coverRef,
        outlineRef,
        geometryRef,
        onPositioned,
    });

    useLayoutEffect(() => {
        const element = dialogRef.current?.firstElementChild as HTMLElement | null;
        if (!mounted || closing || !element) return;
        if (!reducedMotion && element.animate) {
            const animation = element.animate(
                [
                    { opacity: 0, transform: 'translateY(4px)' },
                    { opacity: 1, transform: 'none' },
                ],
                { duration: 180 },
            );
            return () => animation.cancel();
        }
    }, [displayed?.props.index, displayed?.tourId, mounted, closing, reducedMotion, dialogRef]);

    useLayoutEffect(() => {
        if (!mounted || closing || !displayedStep) return;
        // Action steps keep keyboard access to the real target and its dropdowns.
        if (displayedStep.data?.action) {
            const target = resolveTourTarget(displayedStep.data.action.target ?? displayedStep.target);
            const focusTarget = target?.matches('button,input,select,textarea,a[href]')
                ? target
                : target?.querySelector<HTMLElement>('input,select,textarea,button,a[href]');
            focusTarget?.focus({ preventScroll: true });
            return;
        }
        const card = dialogRef.current;
        const focusTarget =
            card?.querySelector<HTMLElement>('[data-action="primary"]') ??
            card?.querySelector<HTMLElement>('.tour-tooltip__branches button') ??
            card?.querySelector<HTMLElement>('button');
        focusTarget?.focus({ preventScroll: true });
    }, [displayedStep, mounted, closing, dialogRef]);

    useEffect(() => {
        if (!ready || !incoming) return;
        const popupEvents = new WeakSet<KeyboardEvent>();
        // Mantine can remove the popup marker in React's handler before native bubbling.
        const captureKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === 'Escape' &&
                event.target instanceof Element &&
                event.target.closest('[data-mantine-stop-propagation="true"]')
            )
                popupEvents.add(event);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !event.defaultPrevented && !popupEvents.has(event)) {
                event.preventDefault();
                incoming.props.controls.skip('button_skip');
            }
        };
        document.addEventListener('keydown', captureKeyDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', captureKeyDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [ready, incoming]);

    if (!mounted || !displayed) return null;
    const data = displayed.props.step.data;
    const disabled = closing || !running || displayed.props.index !== stepIndex;
    return createPortal(
        <TourControlsContext.Provider value={retainedControls.current}>
            <div className="tour-motion-layer" data-closing={closing} data-testid="tour-motion-layer">
                <svg className="tour-motion-layer__overlay" aria-hidden="true" data-hidden={!!data?.hideOverlay}>
                    <path ref={coverRef} className="tour-motion-layer__cover" fillRule="evenodd" />
                    <path ref={outlineRef} className="tour-motion-layer__outline" />
                </svg>
                <div
                    ref={dialogRef}
                    className="tour-motion-layer__card"
                    role="presentation"
                    inert={disabled}
                    data-testid="tour-motion-card"
                    data-step-index={displayed.props.index}
                    onKeyDown={(event) => {
                        if (event.key !== 'Tab' || data?.action || data?.hideOverlay) return;
                        const items = Array.from(
                            event.currentTarget.querySelectorAll<HTMLElement>(
                                'button:not([disabled]),a[href],input,select,textarea',
                            ),
                        ).filter((el) => el.getClientRects().length > 0);
                        const first = items[0],
                            last = items[items.length - 1];
                        if (event.shiftKey && document.activeElement === first) {
                            event.preventDefault();
                            last?.focus();
                        } else if (!event.shiftKey && document.activeElement === last) {
                            event.preventDefault();
                            first?.focus();
                        }
                    }}
                >
                    <TourTooltip {...displayed.props} />
                </div>
            </div>
        </TourControlsContext.Provider>,
        document.body,
    );
}
